import { useEffect, useState } from 'react';
import useUserStore from '@/stores/UserStore';
import { Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { checkIGConnectionStatus, reconnectInstagram } from '@/hooks/user';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useInstagramDirectPopup } from '@/components/onboarding/InstagramDirectPopup';

import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

const InstagramDirect = () => {
  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>Instagram Direct</h3>
          <p className='text-sm text-muted-foreground'>
            Manage your Instagram Direct connection
          </p>
        </div>
      </div>
      <Separator />
      <InstagramDirectConnection />
    </div>
  );
};

export default InstagramDirect;

const InstagramDirectConnection = () => {
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null); // 'connected', 'needs_reconnect', 'not_connected'

  let { user } = useUserStore(
    useShallow((state) => ({
      user: state.user,
    }))
  );

  // Use the popup hook
  const { openInstagramDirectPopup, loading: popupLoading } =
    useInstagramDirectPopup();

  const checkStatus = async () => {
    setLoading(true);
    try {
      await checkIGConnectionStatus();
      setConnectionStatus('connected');
    } catch (error) {
      if (error.response?.status === 422) {
        setConnectionStatus('needs_reconnect');
      } else if (error.response?.status === 404) {
        setConnectionStatus('not_connected');
      } else {
        console.error('Failed to check Instagram Direct status:', error);
        setConnectionStatus('not_connected');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check current connection status
    checkStatus();
  }, []);

  const handleConnect = async () => {
    await openInstagramDirectPopup();
  };

  const handleReconnect = async () => {
    setLoading(true);
    try {
      const response = await reconnectInstagram();

      // Check if we got a reconnect URL in the response
      if (response?.data?.reconnect_url) {
        window.location.href = response.data.reconnect_url;
      } else {
        // If no URL, assume it was successful
        toast.success('Instagram Direct reconnected successfully');
        setConnectionStatus('connected');
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to reconnect Instagram Direct:', error);
      toast.error('Failed to reconnect Instagram Direct');
      setLoading(false);
    }
  };

  const defaultPic = `http://graph.facebook.com/${user?.customer?.fb_id}/picture?type=large`;

  if (loading || popupLoading) {
    return (
      <div className='flex items-center justify-center pt-48'>
        <Loader2Icon className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  const name = user?.customer?.ig_name || '';
  const profilePic = user?.customer?.profile_pic_url || defaultPic;

  return (
    <div className='flex items-center justify-center pt-48'>
      {connectionStatus === 'connected' && user?.customer?.ig_name ? (
        <div className='flex flex-col items-center gap-4 text-center'>
          <Avatar className='h-24 w-24'>
            <AvatarImage src={profilePic} alt={name} />
            <AvatarFallback>{name?.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className='flex flex-col gap-1'>
            <h2 className='text-2xl font-bold'>{name}</h2>
            <span className='text-sm text-muted-foreground'>
              @{user?.customer?.ig_username}
            </span>
          </div>
          <div className='mt-5'>
            <Button
              onClick={handleReconnect}
              disabled={popupLoading}
              className='flex items-center justify-center'
            >
              {popupLoading ? (
                <>
                  <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                  Reconnecting...
                </>
              ) : (
                'Connect / Reconnect'
              )}
            </Button>
          </div>
        </div>
      ) : connectionStatus === 'needs_reconnect' ? (
        <div className='flex flex-col items-center'>
          <span>Your Instagram Direct connection needs to be reconnected</span>
          <div className='mt-5'>
            <Button
              onClick={handleReconnect}
              disabled={popupLoading}
              className='flex items-center justify-center'
            >
              {popupLoading ? (
                <>
                  <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                  Reconnecting...
                </>
              ) : (
                'Reconnect Instagram Direct'
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-center'>
          <span>You have not connected an Instagram Direct Account</span>
          <div className='mt-5'>
            <Button
              onClick={handleConnect}
              disabled={popupLoading}
              className='flex items-center justify-center'
            >
              {popupLoading ? (
                <>
                  <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                  Connecting...
                </>
              ) : (
                'Connect Instagram Direct'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
